import { useState, useEffect } from "react";
import { Star, Send, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SEOHead from "@/components/SEOHead";
import { useLanguage } from "@/lib/LanguageContext";
import api from "@/lib/api";

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, lang } = useLanguage();

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formText, setFormText] = useState("");
  const [formRating, setFormRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : 0;
  const dateLang = lang === "kz" ? "kk-KZ" : "ru-RU";

  const handleSubmit = async () => {
    if (!formName.trim() || !formPhone.trim() || !formText.trim() || formRating === 0) {
      alert(t("reviews.fillAll"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", { client_name: formName, client_phone: formPhone, text: formText, rating: formRating });
      setSubmitted(true);
      setFormName(""); setFormPhone(""); setFormText(""); setFormRating(0);
    } catch {
      alert(t("reviews.reviewError"));
    }
    setSubmitting(false);
  };

  return (
    <div className="py-24 md:py-32" data-testid="reviews-page">
      <SEOHead path="/reviews" />
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-primary font-sans mb-2">{t("nav.reviews")}</p>
            <h1 className="text-5xl md:text-6xl font-serif tracking-tight">{t("reviews.title")}</h1>
          </div>
          {!showForm && !submitted && (
            <Button
              onClick={() => setShowForm(true)}
              className="bg-primary text-primary-foreground rounded-none px-6 py-5 uppercase tracking-widest text-sm hover:bg-primary/90 btn-shine"
              data-testid="write-review-btn"
            >
              {t("reviews.writeReview")}
            </Button>
          )}
        </div>

        {/* Review Form */}
        {submitted ? (
          <div className="bg-primary/10 border border-primary/20 p-6 mb-10 flex items-center gap-3" data-testid="review-submitted">
            <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
              <Check className="w-5 h-5 text-primary" />
            </div>
            <p className="text-sm">{t("reviews.reviewSent")}</p>
          </div>
        ) : showForm ? (
          <div className="bg-card border border-border p-6 mb-10" data-testid="review-form">
            <h3 className="font-serif text-xl mb-5">{t("reviews.writeReview")}</h3>
            <div className="space-y-4 max-w-lg">
              {/* Rating */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">{t("reviews.rating")} *</label>
                <div className="flex gap-1" data-testid="rating-stars">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <button
                      key={i}
                      onClick={() => setFormRating(i)}
                      onMouseEnter={() => setHoverRating(i)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="p-0.5"
                      data-testid={`rating-star-${i}`}
                    >
                      <Star className={`w-7 h-7 transition-colors ${i <= (hoverRating || formRating) ? "text-primary fill-primary" : "text-muted-foreground/30"}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">{t("reviews.yourName")} *</label>
                  <Input
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder={t("common.name")}
                    className="bg-background border-border rounded-none"
                    data-testid="review-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">{t("reviews.yourPhone")} *</label>
                  <Input
                    value={formPhone}
                    onChange={(e) => setFormPhone(e.target.value)}
                    placeholder="+7 7XX XXX XX XX"
                    className="bg-background border-border rounded-none"
                    data-testid="review-phone-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">{t("reviews.yourReview")} *</label>
                <Textarea
                  value={formText}
                  onChange={(e) => setFormText(e.target.value)}
                  placeholder={t("reviews.yourReview")}
                  className="bg-background border-border rounded-none min-h-[100px]"
                  data-testid="review-text-input"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-primary text-primary-foreground rounded-none gap-2 px-6"
                  data-testid="submit-review-btn"
                >
                  <Send className="w-4 h-4" /> {submitting ? "..." : t("reviews.submitReview")}
                </Button>
                <Button variant="ghost" onClick={() => setShowForm(false)} className="text-muted-foreground rounded-none">
                  {t("common.cancel")}
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {reviews.length > 0 && (
          <div className="flex items-center gap-4 mb-10 p-6 bg-card border border-border inline-flex" data-testid="avg-rating">
            <span className="text-4xl font-serif text-primary">{avgRating}</span>
            <div>
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className={`w-4 h-4 ${i <= Math.round(avgRating) ? "text-primary fill-primary" : "text-muted-foreground/30"}`} />
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{reviews.length} {t("reviews.reviewCount")}</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => <div key={i} className="h-32 bg-card animate-pulse" />)}
          </div>
        ) : reviews.length === 0 && !showForm && !submitted ? (
          <div className="text-center py-20" data-testid="no-reviews">
            <Star className="w-12 h-12 text-muted-foreground/20 mx-auto mb-4" />
            <h2 className="font-serif text-2xl mb-2 text-muted-foreground">{t("reviews.noReviews")}</h2>
            <p className="text-sm text-muted-foreground mb-6">{t("reviews.beFirst")}</p>
            <Button onClick={() => setShowForm(true)} className="bg-primary text-primary-foreground rounded-none px-6" data-testid="write-first-review-btn">
              {t("reviews.writeReview")}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {reviews.map((r) => (
              <div key={r.id} className="p-6 bg-card border border-border" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary font-serif text-lg">
                      {r.client_name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-sans text-sm font-medium">{r.client_name}</p>
                      <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString(dateLang)}</p>
                    </div>
                  </div>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className={`w-3.5 h-3.5 ${i <= r.rating ? "text-primary fill-primary" : "text-muted-foreground/30"}`} />
                    ))}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{r.text}</p>
                {r.admin_reply && (
                  <div className="mt-4 pl-4 border-l-2 border-primary/20">
                    <p className="text-xs text-primary mb-1">{t("reviews.reply")}</p>
                    <p className="text-sm text-muted-foreground">{r.admin_reply}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
